import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {AnimUtils} from "../utils/AnimUtils";
import {ExtendedGameMode} from "../server/ExtendedGameMode";
import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {HOTB} from "../HOTB";
import {ExtendedGameModel} from "../server/ExtendedGameModel";
import {gsap} from "gsap";
import {GameUtils} from "../utils/GameUtils";
import {GameConfig} from "../GameConfig";
import {GameSounds} from "../sound/GameSounds";

export class Foreground extends PIXI.Container implements StageLayer {
    private _doorSpine: PIXI.spine.Spine;
    private _doorMask: PIXI.Graphics;
    private _vendingMachineBanner: PIXI.Sprite;

    constructor() {
        super();
        this.name = "Foreground";
        this.init();
    }

    onResize(): void {
        this.setDoorMaskProps();
    }

    public onGameStart(): void {
        const tl: gsap.core.Timeline = gsap.timeline({
            delay: 0.2
        });

        if (HOTB.apiPlugin.isReplay) {
            tl.add(this.showStaticDoors(HOTB.model.data.mode, false));
        } else {
            tl.add(HOTB.gameForeground.handleDoors(true, true));
        }
    }

    public handleDoors(openDoors: boolean, hideDoorsOnEnd: boolean, forceIgnoreSyms: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
                onStart: (): void => {
                    this.showDoors();
                },
                onComplete: (): void => {
                    if (hideDoorsOnEnd) {
                        this.hideDoors();
                    }
                }
            }),
            gameModel: ExtendedGameModel = HOTB.model,
            gameMode: ExtendedGameMode = (openDoors ? gameModel.data.nextMode : gameModel.data.mode),
            gameTypePrefix: string = GameUtils.getGamePrefix(gameMode),
            animName: string = gameTypePrefix + "_door" + (openDoors ? "Open" : "Close");

        if (!forceIgnoreSyms) {
            tl.add((): void => {
                if (openDoors) {
                    GameUtils.toggleSymbols(true);
                }
            });
        }

        tl.add([
            AnimUtils.playSpineAnimation({
                spineObj: this._doorSpine,
                animation: animName
            }),
            (): void => {
                this.playDoorSounds(animName);
            }
        ]);

        if (!forceIgnoreSyms) {
            tl.add((): void => {
                if (!openDoors) {
                    GameUtils.toggleSymbols(false);
                }
            }, (10 / GameConfig.defaultFPS));
        }

        return tl;
    }

    public showStaticDoors(mode: ExtendedGameMode, isClosed: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            animName: string = GameUtils.getGamePrefix(mode);

        tl.add(AnimUtils.playSpineAnimation({
            spineObj: this._doorSpine,
            animation: animName + "_static" + (isClosed ? "Close" : "Open")
        }));

        tl.add((): void => {
            this.showDoors();
        });

        return tl;
    }

    public hideDoors(): void {
        this._doorSpine.alpha = 0;
    }

    public toggleMachineBanner(show: boolean, dur: number = 0.01): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.to(this._vendingMachineBanner, {
            alpha: show ? 1 : 0,
            duration: dur
        });

        return tl;
    }

    public getDoorsAnimDur(animName: string): number {
        return AnimUtils.getSpineDuration(this._doorSpine, animName);
    }

    private playDoorSounds(type: string): void {
        if (type === "mg_doorOpen") {
            HOTB.soundPlayer.playSound(GameSounds.MAIN_DOOR_OPEN);
        }

        if (type === "mg_doorClose") {
            HOTB.soundPlayer.playSound(GameSounds.MAIN_DOOR_CLOSE);
        }

        if (type === "fs_doorOpen" || type === "sfs_doorOpen") {
            HOTB.soundPlayer.playSound(GameSounds.BONUS_DOOR_OPEN);
        }

        if (type === "fs_doorClose" || type === "sfs_doorClose") {
            HOTB.soundPlayer.playSound(GameSounds.BONUS_DOOR_CLOSE);
        }
    }

    private showDoors(): void {
        this._doorSpine.alpha = 1;
    }

    private init(): void {
        this.createDoors();
        this.createDoorsMask();
        this.createMachineBanner();
        this.onResize();
    }

    private createDoors(): void {
        this._doorSpine = AnimUtils.createNewSpine({
            path: "bgDoors",
            name: "bgDoors",
            pos: new PIXI.Point(360, 360),
            initShow: true
        });
        this.addChild(this._doorSpine);
    }

    private createDoorsMask(): void {
        this._doorMask = new PIXI.Graphics();
        this.setDoorMaskProps();
        this._doorSpine.mask = this._doorMask;
        this.addChild(this._doorMask);
    }

    private createMachineBanner(): void {
        this._vendingMachineBanner = new PIXI.Sprite(HOTB.assetLoader.getTextures("machineBannerImg")[0]);
        this._vendingMachineBanner.anchor.set(0.5);
        this._vendingMachineBanner.position.set(360, 460);
        this._vendingMachineBanner.alpha = 0;
        this.addChild(this._vendingMachineBanner);
    }

    private setDoorMaskProps(): void {
        if (this._doorMask) {
            const bounds: ScreenBounds = NolimitApplication.screenBounds;

            this._doorMask.clear()
                .beginFill(0xFFFF00, 0.5)
                .drawRect(0, bounds.top, 520, bounds.height)
                .endFill();

            this._doorMask.position.set(100, 0);
        }
    }
}